import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClient } from '../../hooks/useClient';
import { CircularProgress, Box } from '@mui/material';
import { useAuthProvider } from '../../auth/hooks/auth-provider';
import { UserType } from '../../models/UserType';

/**
 * Props for the ClientOnboardingCheck component
 */
interface ClientOnboardingCheckProps {
  children: React.ReactNode;
}

/**
 * Component that checks if a client user has completed onboarding
 * If not, redirects them to the onboarding page
 */
const ClientOnboardingCheck: React.FC<ClientOnboardingCheckProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user } = useAuthProvider();
  const { data: clientProfile, isLoading, error } = useClient();

  useEffect(() => {
    // Only redirect client users who haven't completed onboarding
    if (!isLoading && !error && user.type === UserType.Client) {
      // No profile or onboarding not completed - redirect to onboarding
      if (!clientProfile || !clientProfile.onboardingCompleted) {
        navigate('/profile/onboarding');
      }
    }
  }, [clientProfile, isLoading, error, user.type, navigate]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // If it's a client user who hasn't completed onboarding, don't render children
  if (user.type === UserType.Client && (!clientProfile || !clientProfile.onboardingCompleted)) {
    return null;
  }

  // Otherwise, render children
  return <>{children}</>;
};

export default ClientOnboardingCheck;